// @ts-ignore
import Vue from "vue";
import { get } from "lodash";
import { vueSet } from "vue-deepset";

function arrayMove(arr: any[], fromIndex: number, toIndex: number) {
	const element = arr[fromIndex];
	arr.splice(fromIndex, 1);
	arr.splice(toIndex, 0, element);
}

const isBlank = (val: any) => val == null;

// This ugly thing allows things to be undef.
const getInitialValue = (state: any, form: any, field: any, _default: any) => {
	const existingValue = get(state[form].values, field);
	if (!isBlank(existingValue)) return existingValue;

	const initialValue = get(state[form], `initialValues.${field}`);
	if (!isBlank(initialValue)) return initialValue;

	return _default;
};

export default {
	state: {},
	mutations: {
		form_register(state: any, payload: any) {
			const {
				form, // String of the form name, used to reference the instance of the form.
				initialValues, // Object of field name pointing to the initial values.
				validation = {} // Object containing vaidation errors.
			} = payload;
			vueSet(state, form, {
				error: null,
				focused: null,
				registeredFields: {},
				initialValues, // so we can reset later
				values: {},
				fieldErrors: validation,
				meta: {},
				touched: false,
				submitting: false,
				submitted: false
			});
		},
		setFormFieldValue(state: any, payload: any) {
			state[`${payload.form}.values.${payload.field}`] = payload.val;
		},
		form_unregister(state: any, payload: any) {
			// TODO: pass options to clear or not!
			const { form } = payload;
			delete state[form];
		},
		form_field_register(state: any, payload: any) {
			const { form, field } = payload;
			// A field will have a path as a name.
			Vue.set(state[form].registeredFields, field, {
				type: "Field"
			});

			vueSet(state[form].meta, field, {
				focused: false,
				touched: false,
				changed: false
			});
			const value = getInitialValue(state, form, field, []);
			vueSet(state[form].values, field, value);
		},
		form_field_unregister(state: any, payload: any) {
			const { form, field } = payload;
			state[form] && delete state[form].registeredFields[field];
		},
		form_field_array_register(state: any, payload: any) {
			const { form, field } = payload;
			Vue.set(state[form].registeredFields, field, {
				type: "FieldArray"
			});
			vueSet(state[form].meta, field, []);

			const value = getInitialValue(state, form, field, []);
			vueSet(state[form].values, field, value);

			value.forEach(() => {
				const newMeta = {
					focused: false,
					touched: false,
					changed: false
				};
				state[form].meta[field].push(newMeta);
			});
		},
		form_field_array_unregister(state: any, payload: any) {
			const { form, field } = payload;
			state[form] && delete state[form].registeredFields[field];
		},
		form_field_array_push(state: any, payload: any) {
			const { form, field, value } = payload;
			const newMeta = {
				focused: false,
				touched: false,
				changed: false
			};
			state[form].meta[field].push(newMeta);
			if (value) {
				state[form].values[field].push(value);
			}
		},
		form_field_array_remove(state: any, payload: any) {
			const { form, field, index } = payload;
			state[form].meta[field].splice(index, 1);
			state[form].values[field].splice(index, 1);
			state[form].fieldErrors[field] &&
				state[form].fieldErrors[field].splice(index, 1);
		},
		form_field_array_move(state: any, payload: any) {
			const { form, field, from, to } = payload;
			arrayMove(state[form].values[field], from, to);
			arrayMove(state[form].meta[field], from, to);
			state[form].fieldErrors[field] &&
				arrayMove(state[form].fieldErrors[field], from, to);
		},
		form_field_change(state: any, payload: any) {
			const { form, field, value } = payload;
			vueSet(state[form].values, field, value);
			vueSet(state[form].meta, `${field}.changed`, true);
		},
		form_field_focus(state: any, payload: any) {
			const { form, field } = payload;
			state[form].focused = field;
			vueSet(state[form].meta, `${field}.focused`, true);
		},
		form_field_leave(state: any, payload: any) {
			const { form, field, value } = payload;
			state[form].focused = null;
			vueSet(state[form].values, field, value);
			vueSet(state[form].meta, `${field}.focused`, false);
			vueSet(state[form].meta, `${field}.touched`, true);
		},
		form_reset(state: any, payload: any) {
			const {
				form, // String of the form name, used to reference the instance of the form.
				validation = {} // Object containing vaidation errors.
			} = payload;
			vueSet(state, form, {
				error: null,
				focused: null,
				registeredFields: {},
				values: {},
				fieldErrors: validation,
				meta: {},
				touched: false,
				submitting: false,
				submitted: false
			});
		},
		form_field_reset(state: any, payload: any) {
			const { form, field } = payload;
			const initialValue = get(state[form], `initialValues.${field}`);
			vueSet(state[form].values, field, initialValue);
			vueSet(state[form].meta, `${field}.touched`, false);
		},
		form_validation_update(state: any, payload: any) {
			const { form, validation } = payload;
			vueSet(state[form], "fieldErrors", validation);
		},
		// Just let the form know we've hit submit
		form_submit(state: any, payload: any) {
			const { form } = payload;
			state[form].submitted = true;
			Object.keys(state[form].registeredFields).forEach((field) => {
				// FieldArray ATM doesn't have meta info so only do fields
				if (state[form].registeredFields[field].type === "Field") {
					vueSet(state[form].meta, `${field}.touched`, true);
				}
			});
		},
		form_submit_server_start(state: any, payload: any) {
			const { form } = payload;
			state[form].submitting = true;
			state[form].error = null;
		},
		form_submit_server_success(state: any, payload: any) {
			const { form } = payload;
			state[form].submitting = false;
			state[form].error = null;
		},
		form_submit_server_failure(state: any, payload: any) {
			const { form, error } = payload;
			state[form].submitting = false;
			state[form].error = error;
		}
	},
	getters: {
		formIsValid: (state: any, getters: any) => (form: any) => {
			let valid = true;
			Object.keys(state[form].registeredFields).forEach((field) => {
				if (state[form].registeredFields[field].type === "FieldArray") return;
				if (getters.formFieldError(form, field)) {
					valid = false;
				}
			});
			return valid;
		},
		getForm: (state: any) => (form: any) => {
			return state[form];
		},
		formExists: (state: any) => (form: any) => {
			return !!state[form];
		},
		formHasSubmitted: (state: any) => (form: any) => {
			return state[form].submitted;
		},
		// Returns the names of all the fields in this array
		formArraySubFields: (state: any) => (form: any, field: any) => {
			return state[form].meta[field].map((meta: any, index: any) => {
				return `${field}[${index}]`;
			});
		},
		formFieldValue: (state: any) => (form: any, field: any) => {
			return get(state, `${form}.values.${field}`);
		},
		formFieldError: (state: any) => (form: any, field: any) => {
			return get(state, `${form}.fieldErrors.${field}`, null);
		},
		formValues: (state: any) => (form: any) => {
			return get(state, `${form}.values`, {});
		},
		formGetFieldMeta: (state: any) => (form: any, field: any) => {
			return get(state[form].meta, field, {});
		},
		formError: (state: any) => (form: any) => {
			return state[form].error;
		},
		formSubmitting: (state: any) => (form: any) => {
			return state[form].submitting;
		}
	}
};
